import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openPayingCustomersModal } from '@/components/paying-customers-only-modal';
import { Button, TextInput, Loader, ActionIcon, Tooltip, Alert, Checkbox, NumberInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useCreatePayment, useGenerateAfipInvoice, useUpdatePayment } from '@/hooks/api-hooks';
import { Business, Payment } from '@turizi/shared';
import { DEFAULT_CURRENCY, PAYER_TYPES } from '@turizi/shared';
import BusinessSelector from '@/components/business-selector';
import { MoneyNumberInput, PaymentMethodSelect } from '@/components/snippets';
import { localTenant } from '@/services/session-service';
import { notify } from '@/lib/notify';
import { IconCalendar, IconExternalLink, IconAlertCircle, IconArrowRight, IconBuildingStore } from '@tabler/icons-react';
import CurrencySelector from './currency-selector';
import { isTier1Tenant } from '@/lib/format-utils';
import AfipInvoiceFields from './afip-invoice-fields';

const EMPTY_PAYMENT: Partial<Payment> = {
    currency: DEFAULT_CURRENCY,
    payment_method: 'bank_transfer',
    attachments: [],
    notes: '',
    status: 'pending',
};

interface NewPaymentRequestModalProps {
    onClose: () => void;
    initialFromClient?: Business | null;
    initialToClient?: Business | null;
    initialAffectedReservations?: number[];
    initialAmount?: number;
    initialCurrency?: string;
    onSuccess?: (payment: Payment) => void;
    forceDirection?: 'IN' | 'OUT';
}

export default function NewPaymentRequestModal({
    onClose,
    initialFromClient,
    initialToClient,
    initialAffectedReservations,
    initialAmount,
    initialCurrency,
    onSuccess,
    forceDirection
}: NewPaymentRequestModalProps) {
    const tenant = localTenant();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (!isTier1Tenant()) {
            openPayingCustomersModal();
            if (onClose) onClose();
        }
    }, [onClose]);

    const [savedPayment, setSavedPayment] = useState<Payment | null>(null);

    const defaultTenantBusiness = useMemo<Business | null>(() => {
        if (tenant?.businesses && tenant.businesses.length > 0) {
            return tenant.businesses[0];
        }
        if (tenant?.id && tenant?.name) {
            return {
                id: tenant.id,
                name: tenant.name,
                type: PAYER_TYPES.BUSINESS
            } as Business;
        }
        return null;
    }, [tenant]);

    const [direction, setDirection] = useState<'IN' | 'OUT'>(() => {
        if (forceDirection) return forceDirection;
        if (initialFromClient && !initialToClient) return 'OUT';
        return 'IN';
    });

    const [myCompany, setMyCompany] = useState<Business | null>(() => {
        return defaultTenantBusiness;
    });

    const [client, setClient] = useState<Business | null>(() => {
        if (initialToClient) return initialToClient;
        if (initialFromClient) return initialFromClient;
        return null;
    });

    const isSameOriginDestination = useMemo(() => {
        return !!client && !!myCompany && client.type === PAYER_TYPES.BUSINESS && client.id === myCompany.id;
    }, [client, myCompany]);

    const createMutation = useCreatePayment();
    const generateInvoiceMutation = useGenerateAfipInvoice();
    const updatePaymentMutation = useUpdatePayment();

    const [payment, setPayment] = useState<Partial<Payment>>(() => ({
        ...EMPTY_PAYMENT,
        amount: initialAmount ?? undefined,
        currency: initialCurrency ?? DEFAULT_CURRENCY,
    }));

    const [generateTaxVoucher, setGenerateTaxVoucher] = useState(false);
    const [afipReceiverName, setAfipReceiverName] = useState('');
    const [afipDocTipo, setAfipDocTipo] = useState('99');
    const [afipDocNro, setAfipDocNro] = useState('0');
    const [afipInvoiceType, setAfipInvoiceType] = useState('6');
    const [afipPos, setAfipPos] = useState<number | null>(null);
    const [afipConcepto, setAfipConcepto] = useState('2');
    const [afipDescription, setAfipDescription] = useState('');
    const [afipNetAmount, setAfipNetAmount] = useState<string | number>('');
    const [afipIvaRate, setAfipIvaRate] = useState('5');
    const [afipIvaAmount, setAfipIvaAmount] = useState<number>(0);
    const [afipTotalAmount, setAfipTotalAmount] = useState<number>(0);

    const [afipFchServDesde, setAfipFchServDesde] = useState<Date | null>(new Date());
    const [afipFchServHasta, setAfipFchServHasta] = useState<Date | null>(new Date());
    const [afipFchVtoPago, setAfipFchVtoPago] = useState<Date | null>(new Date());
    const [afipTaxCondition, setAfipTaxCondition] = useState<string>('5');

    // Auto-update AFIP fields when client changes
    useEffect(() => {
        if (client) {
            setAfipReceiverName(client.name || '');
            const detectedTaxCondition = client.tax_condition || '5';
            setAfipDocTipo('80');
            setAfipDocNro(client.tax_id_number || '');
            setAfipTaxCondition(detectedTaxCondition);
            setAfipInvoiceType(detectedTaxCondition === '1' ? '1' : '6');
        } else {
            setAfipReceiverName('');
            setAfipDocTipo('99');
            setAfipDocNro('0');
            setAfipTaxCondition('5');
            setAfipInvoiceType('6');
        }
    }, [client]);

    // Handle docTipo = 99 docNro reset
    useEffect(() => {
        if (afipDocTipo === '99') {
            setAfipDocNro('0');
            setAfipTaxCondition('5');
        } else if (afipDocNro === '0') {
            setAfipDocNro('');
        }
    }, [afipDocTipo]);

    // Auto-calculate Net / IVA / Total from payment amount
    useEffect(() => {
        const amt = Number(payment.amount || 0);
        if (amt > 0) {
            if (afipInvoiceType === '11') {
                setAfipNetAmount(amt);
                setAfipIvaAmount(0);
                setAfipTotalAmount(amt);
            } else {
                let rate = 0.21;
                if (afipIvaRate === '4') rate = 0.105;
                if (afipIvaRate === '6') rate = 0.27;
                if (afipIvaRate === '3') rate = 0;

                const net = Math.round((amt / (1 + rate)) * 100) / 100;
                const iva = Math.round((amt - net) * 100) / 100;
                setAfipNetAmount(net);
                setAfipIvaAmount(iva);
                setAfipTotalAmount(amt);
            }
        } else {
            setAfipNetAmount('');
            setAfipIvaAmount(0);
            setAfipTotalAmount(0);
        }
    }, [payment.amount, afipIvaRate, afipInvoiceType]);



    const handleSubmit = () => {
        const payer = direction === 'IN' ? client : myCompany;
        const payee = direction === 'IN' ? myCompany : client;

        const performDbInsert = (attachmentsList: string[] = []) => {
            createMutation.mutate({
                ...payment,
                status: 'pending',
                expected_amount: payment.amount,
                amount: 0,
                due_date: payment.due_date,
                payment_date: null,
                from_person_id: payer?.type === PAYER_TYPES.PERSON ? payer.id : null,
                from_business_id: payer?.type === PAYER_TYPES.BUSINESS ? payer.id : null,
                to_person_id: payee?.type === PAYER_TYPES.PERSON ? payee.id : null,
                to_business_id: payee?.type === PAYER_TYPES.BUSINESS ? payee.id : null,
                attachments: attachmentsList,
                affected_reservations: [],
            }, {
                onSuccess: ({ data: createdPayment }) => {
                    setSavedPayment(createdPayment);
                    if (onSuccess) onSuccess(createdPayment);
                }
            });
        };

        if (generateTaxVoucher) {
            if (!afipPos || afipPos <= 0) {
                notify.error(t('pos_required_error'), t('error'));
                return;
            }
            const cleanDocNro = afipDocNro.replace(/\D/g, '');
            const data = {
                PtoVta: afipPos,
                CbteTipo: parseInt(afipInvoiceType, 10),
                Concepto: parseInt(afipConcepto, 10),
                description: afipDescription.trim() || undefined,
                DocTipo: parseInt(afipDocTipo, 10),
                DocNro: afipDocTipo === '99' ? 0 : parseInt(cleanDocNro, 10) || 0,
                CondicionIVAReceptorId: afipDocTipo === '99' ? 5 : parseInt(afipTaxCondition, 10) || 5,
                receiver_name: afipReceiverName || 'Consumidor Final',
                ImpTotal: afipTotalAmount,
                ImpNeto: typeof afipNetAmount === 'number' ? afipNetAmount : parseFloat(afipNetAmount) || afipTotalAmount,
                ImpIVA: afipInvoiceType === '11' ? 0 : afipIvaAmount,
                ivaId: afipInvoiceType === '11' ? null : parseInt(afipIvaRate, 10),
                FchServDesde: (afipConcepto === '2' || afipConcepto === '3') && afipFchServDesde ? afipFchServDesde.toISOString().split('T')[0] : undefined,
                FchServHasta: (afipConcepto === '2' || afipConcepto === '3') && afipFchServHasta ? afipFchServHasta.toISOString().split('T')[0] : undefined,
                FchVtoPago: (afipConcepto === '2' || afipConcepto === '3') && afipFchVtoPago ? afipFchVtoPago.toISOString().split('T')[0] : undefined,
                business_id: client?.id
            };

            generateInvoiceMutation.mutate({
                tenantId: tenant.id,
                data
            }, {
                onSuccess: (afipRes: any) => {
                    notify.success(t('invoice_generated_success'), t('success'));
                    const downloadUrl = `${import.meta.env.VITE_API_BASE_URL}/afip/vouchers/${afipRes.voucherId}/download`;
                    performDbInsert([downloadUrl]);
                },
                onError: (err: any) => {
                    const msg = err.response?.data?.message || err.message || t('error');
                    notify.error(`${t('invoice_generation_failed')}: ${msg}`, t('error'));
                }
            });
        } else {
            performDbInsert();
        }
    };

    if (!isTier1Tenant()) {
        return null;
    }

    return (
        <div className="w-[500px] max-w-full mx-auto">
            <div className="mx-auto">
                {/* Main Stack layout */}
                <div className="space-y-4">
                    {/* Payment Details & Direction */}
                    <div className="space-y-4">
                        {/* Payer/Payee */}
                        {(() => {
                            const ourBusinessBlock = (
                                <div className="flex-1 space-y-1">
                                    <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                                        {direction === 'IN' ? t('payee') : t('payer')}
                                    </div>
                                    {myCompany?.name ? (
                                        <div className="h-10 px-3 bg-gray-100 rounded-md font-bold text-sm text-gray-700 flex items-center gap-2">
                                            <IconBuildingStore size={18} className="text-gray-500" />
                                            <span className="truncate">{myCompany.name}</span>
                                        </div>
                                    ) : (
                                        <Alert color="red" icon={<IconAlertCircle size={16} />} radius="md" p="xs" className="text-xs">
                                            {t('no_tenant_business_error')}
                                        </Alert>
                                    )}
                                </div>
                            );

                            const clientSelectorBlock = (
                                <div className="flex-1 space-y-1">
                                    <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                                        {direction === 'IN' ? t('payer') : t('payee')}
                                    </div>
                                    <BusinessSelector
                                        placeholder={<div className='w-full text-sm text-gray-400 p-1'>{t('select_client')}</div>}
                                        onSelect={setClient}
                                        selected={client}
                                        className='font-bold'
                                    />
                                </div>
                            );

                            const leftColumn = direction === 'IN' ? clientSelectorBlock : ourBusinessBlock;
                            const rightColumn = direction === 'IN' ? ourBusinessBlock : clientSelectorBlock;

                            return (
                                <div className="flex gap-4 items-center p-4 bg-gray-50 border border-gray-200 rounded-xl">
                                    {/* Left column: Payer (Sender) */}
                                    {leftColumn}

                                    {/* Middle: Arrow indicating flow (Payer -> Payee, always pointing right) */}
                                    <div className="flex items-center justify-center pt-4 z-10">
                                        <Tooltip label={direction === 'IN' ? t('money_in') : t('money_out')} disabled={!!forceDirection}>
                                            <ActionIcon
                                                onClick={forceDirection ? undefined : () => setDirection(prev => prev === 'IN' ? 'OUT' : 'IN')}
                                                color={direction === 'IN' ? 'green' : 'red'}
                                                variant="light"
                                                size="lg"
                                                radius="xl"
                                                className="border border-gray-200"
                                                style={forceDirection ? { cursor: 'default', pointerEvents: 'none' } : undefined}
                                            >
                                                <IconArrowRight size={20} className="stroke-[2.5]" />
                                            </ActionIcon>
                                        </Tooltip>
                                    </div>

                                    {/* Right column: Payee (Receiver) */}
                                    {rightColumn}
                                </div>
                            );
                        })()}

                        {isSameOriginDestination && (
                            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                                {t('same_origin_destination_warning')}
                            </Alert>
                        )}

                        {/* Payment Details Box */}
                        <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-xl bg-white shadow-xs">
                            <PaymentMethodSelect
                                label={t('method')}
                                value={payment.payment_method}
                                onChange={(val) => setPayment(p => ({ ...p, payment_method: val }))}
                                className='w-full'
                            />

                            <DateInput
                                leftSection={<IconCalendar size={14} color='gray' stroke={1} />}
                                highlightToday={true}
                                label={t('due_date')}
                                valueFormat='DD, MMM YYYY'
                                value={payment.due_date}
                                onChange={(newDate) => setPayment(p => ({ ...p, due_date: newDate }))}
                                locale={i18n.language}
                                className='w-full'
                            />

                            <div className="grid grid-cols-2 gap-4">
                                <CurrencySelector
                                    label={t('currency')}
                                    value={payment.currency}
                                    onChange={(val) => setPayment(p => ({ ...p, currency: val }))}
                                    className='w-full'
                                />
                                <MoneyNumberInput
                                    size='md'
                                    className='font-semibold'
                                    amount={payment.amount}
                                    onChange={(val) => setPayment(p => ({ ...p, amount: val }))}
                                    label={t('amount')}
                                />
                            </div>

                            <TextInput
                                label={t('notes')}
                                value={payment.notes || ''}
                                onChange={(e) => setPayment(p => ({ ...p, notes: e.target.value }))}
                            />

                            <Checkbox
                                label={t('generate_tax_voucher')}
                                checked={generateTaxVoucher}
                                onChange={(e) => setGenerateTaxVoucher(e.currentTarget.checked)}
                                className="mt-2"
                            />
                        </div>

                        {/* AFIP Invoice section */}
                        {generateTaxVoucher && (
                            <div className="solid-box-border-tinted space-y-4 p-4 mt-2 bg-gray-50/50">
                                <span className="text-xs font-bold text-gray-700 tracking-wide uppercase">
                                    {t('invoice_details_section')}
                                </span>
                                <AfipInvoiceFields
                                    receiverName={afipReceiverName}
                                    setReceiverName={setAfipReceiverName}
                                    docTipo={afipDocTipo}
                                    setDocTipo={setAfipDocTipo}
                                    docNro={afipDocNro}
                                    setDocNro={setAfipDocNro}
                                    invoiceType={afipInvoiceType}
                                    setInvoiceType={setAfipInvoiceType}
                                    pos={afipPos}
                                    setPos={setAfipPos}
                                    concepto={afipConcepto}
                                    setConcepto={setAfipConcepto}
                                    description={afipDescription}
                                    setDescription={setAfipDescription}
                                    ivaRate={afipIvaRate}
                                    setIvaRate={setAfipIvaRate}
                                    netAmount={afipNetAmount}
                                    ivaAmount={afipIvaAmount}
                                    totalAmount={afipTotalAmount}
                                    fchServDesde={afipFchServDesde}
                                    setFchServDesde={setAfipFchServDesde}
                                    fchServHasta={afipFchServHasta}
                                    setFchServHasta={setAfipFchServHasta}
                                    fchVtoPago={afipFchVtoPago}
                                    setFchVtoPago={setAfipFchVtoPago}
                                    taxCondition={afipTaxCondition}
                                    setTaxCondition={setAfipTaxCondition}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-end gap-6 mt-6 pt-4 border-t border-gray-100">
                    <Button
                        onClick={onClose}
                        color="black"
                        size="xs"
                        variant='subtle'
                    >
                        {t('close')}
                    </Button>
                    {savedPayment ? (
                        <Link
                            to={`/payments/${savedPayment.id}`}
                            className='text-xs font-semibold'
                        >
                            <Button
                                color="blue"
                                size="xs"
                                variant='subtle'
                                onClick={onClose}
                                rightSection={<IconExternalLink size={12} />}
                            >
                                {t('view_payment')}
                            </Button>
                        </Link>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            loading={createMutation.isPending || generateInvoiceMutation.isPending || updatePaymentMutation.isPending}
                            color="black"
                            size="xs"
                            disabled={
                                !myCompany ||
                                !myCompany.name ||
                                !payment.amount ||
                                !payment.payment_method ||
                                !payment.due_date ||
                                isSameOriginDestination ||
                                (generateTaxVoucher && (
                                    !afipReceiverName.trim() ||
                                    (afipDocTipo !== '99' && (!afipDocNro.replace(/\D/g, '') || isNaN(parseInt(afipDocNro.replace(/\D/g, ''), 10)) || parseInt(afipDocNro.replace(/\D/g, ''), 10) <= 0)) ||
                                    !(afipTotalAmount > 0)
                                ))
                            }
                        >
                            {t('payment_request')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
